// ============================================================
// DM-LMS — useApi Hook
// DRY: one fetch/mutation helper for all role pages, following
// the same raw-fetch + envelope-unwrap pattern as useAuth.
// ============================================================

import { useCallback, useEffect, useState } from "react";
import type { ApiResponse } from "@dm-lms/shared/types";

// Absolute in staging/production (web and API are on different origins);
// empty in local dev, where Vite's dev server proxies /api to the Worker.
export const API_URL = import.meta.env.VITE_API_URL ?? "";

interface FetchState<T> {
  data:      T | null;
  isLoading: boolean;
  error:     string | null;
}

// Pass `url: null` to skip fetching (e.g. until a dependent selection is made).
export function useFetch<T>(url: string | null): FetchState<T> & { refetch: () => void } {
  const [state, setState] = useState<FetchState<T>>({ data: null, isLoading: url !== null, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!url) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    async function run() {
      try {
        const res = await fetch(`${API_URL}${url}`, { credentials: "include" });
        const json = (await res.json()) as ApiResponse<T>;
        if (cancelled) return;
        if (!json.ok) {
          setState({ data: null, isLoading: false, error: json.error });
          return;
        }
        setState({ data: json.data, isLoading: false, error: null });
      } catch {
        if (!cancelled) setState({ data: null, isLoading: false, error: "Network error" });
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [url, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, refetch };
}

export async function getJson<T>(url: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, { credentials: "include" });
  return (await res.json()) as ApiResponse<T>;
}

export async function postJson<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, {
    method:      "POST",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
    body:        JSON.stringify(body ?? {}),
  });
  return (await res.json()) as ApiResponse<T>;
}

export async function patchJson<T>(url: string, body: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json" },
    body:        JSON.stringify(body),
  });
  return (await res.json()) as ApiResponse<T>;
}
